import { Box, HStack, Text, Button, IconButton, Flex } from '@chakra-ui/react'
import { Link, useNavigate } from 'react-router-dom'
import { FiShoppingCart, FiMenu, FiX, FiLogOut, FiPackage, FiGrid, FiSettings, FiBookmark, FiUser, FiMessageSquare } from 'react-icons/fi'
import { useState, useEffect } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useCart } from '../context/CartContext'
import { useApiCall } from '../utils/api'
import BrandLogo from '../components/BrandLogo'

export const HEADER_HEIGHT = 72

const NAV = [
  { label: 'Fiyat Hesapla', to: '/tr/yukle' },
  { label: 'Model Pazarı', to: '/tr/pazar' },
  { label: 'Tasarımcılar', to: '/tr/tasarimcilar' },
  { label: 'Firmalar', to: '/tr/firmalar' },
  { label: 'Hizmetler', to: '/tr/hizmetler' },
  { label: 'Yardım', to: '/tr/yardim' },
]

export default function Header() {
  const { user, signout } = useAuth()
  const { itemCount } = useCart()
  const apiCall = useApiCall()
  const [menuOpen, setMenuOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [companyUnreadCount, setCompanyUnreadCount] = useState(0)
  const navigate = useNavigate()
  const isAdmin = user?.is_admin === true
  const isPrintCompany = user?.account_type === 'print_company'
  const isDesigner = user?.is_designer === true

  useEffect(() => {
    if (!user) return

    const fetchUnread = () => {
      if (isPrintCompany) {
        apiCall('/companies/me/messages/unread-count')
          .then((r) => r.json())
          .then((d) => setCompanyUnreadCount(d.count || 0))
          .catch(() => {})
      } else {
        // Fetch direct messages unread count
        const dmPromise = apiCall('/messages/unread-count')
          .then((r) => r.json())
          .then((d) => d.count || 0)
          .catch(() => 0)

        // If designer, also fetch pending commission count
        const commissionPromise = isDesigner
          ? apiCall('/commissions/unread-count')
            .then((r) => r.json())
            .then((d) => d.count || 0)
            .catch(() => 0)
          : Promise.resolve(0)

        Promise.all([dmPromise, commissionPromise]).then(([dm, comm]) => {
          setUnreadCount(dm + comm)
        })
      }
    }

    fetchUnread()
    const interval = setInterval(fetchUnread, 30000)
    return () => clearInterval(interval)
  }, [user, isPrintCompany, isDesigner])

  return (
    <Box
      as="header"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      h={`${HEADER_HEIGHT}px`}
      bg="rgba(0,0,0,0.92)"
      borderBottom="1px solid rgba(255,255,255,0.06)"
      style={{ backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      px={{ base: 4, md: 8 }}
    >
      <Flex h="full" align="center" justify="space-between" maxW="1400px" mx="auto">
        {/* Logo */}
        <BrandLogo size={50} wordmarkSize="lg" />

        {/* Desktop Nav */}
        <HStack gap={1} display={{ base: 'none', md: 'flex' }}>
          {NAV.map((link) => (
            <Button
              key={link.to}
              asChild
              variant="ghost"
              size="sm"
              color="rgba(255,255,255,0.6)"
              _hover={{ color: 'white', bg: 'rgba(255,255,255,0.05)' }}
              borderRadius="6px"
              fontWeight="400"
            >
              <Link to={link.to}>{link.label}</Link>
            </Button>
          ))}
        </HStack>

        {/* Right actions */}
        <HStack gap={2}>
          {/* Cart */}
          <Box position="relative">
            <IconButton
              aria-label="Sepet"
              variant="ghost"
              color="rgba(255,255,255,0.6)"
              _hover={{ color: 'white', bg: 'rgba(255,255,255,0.05)' }}
              onClick={() => navigate('/tr/sepet')}
              borderRadius="6px"
            >
              <FiShoppingCart size={18} />
            </IconButton>
            {itemCount > 0 && (
              <Box
                position="absolute"
                top="-2px"
                right="-2px"
                w={4}
                h={4}
                bg="#CCF301"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="9px" fontWeight="700" color="black">{itemCount}</Text>
              </Box>
            )}
          </Box>

          {user ? (
            <>
              <HStack gap={1} display={{ base: 'none', sm: 'flex' }}>
                {isAdmin && (
                  <IconButton aria-label="Admin" variant="ghost" color="rgba(255,255,255,0.6)" _hover={{ color: '#CCF301', bg: 'rgba(255,255,255,0.05)' }} onClick={() => navigate('/tr/admin')} borderRadius="6px">
                    <FiSettings size={16} />
                  </IconButton>
                )}
                {isPrintCompany ? (
                  <>
                    <IconButton aria-label="Firma Paneli" variant="ghost" color="rgba(255,255,255,0.6)" _hover={{ color: '#CCF301', bg: 'rgba(255,255,255,0.05)' }} onClick={() => navigate('/tr/firma')} borderRadius="6px">
                      <FiGrid size={16} />
                    </IconButton>
                    <Box position="relative">
                      <IconButton aria-label="Firma Mesajları" variant="ghost" color="rgba(255,255,255,0.6)" _hover={{ color: '#CCF301', bg: 'rgba(255,255,255,0.05)' }} onClick={() => navigate('/tr/firma/mesajlar')} borderRadius="6px">
                        <FiMessageSquare size={16} />
                      </IconButton>
                      {companyUnreadCount > 0 && (
                        <Box
                          position="absolute" top="-2px" right="-2px"
                          w={4} h={4} bg="#CCF301" borderRadius="full"
                          display="flex" alignItems="center" justifyContent="center"
                        >
                          <Text fontSize="9px" fontWeight="700" color="black">
                            {companyUnreadCount > 9 ? '9+' : companyUnreadCount}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <IconButton aria-label="Siparişlerim" variant="ghost" color="rgba(255,255,255,0.6)" _hover={{ color: 'white', bg: 'rgba(255,255,255,0.05)' }} onClick={() => navigate('/tr/siparislerim')} borderRadius="6px">
                      <FiPackage size={16} />
                    </IconButton>
                    <IconButton aria-label="Modellerim" variant="ghost" color="rgba(255,255,255,0.6)" _hover={{ color: 'white', bg: 'rgba(255,255,255,0.05)' }} onClick={() => navigate('/tr/modellerim')} borderRadius="6px">
                      <FiGrid size={16} />
                    </IconButton>
                    <IconButton aria-label="Kaydedilenler" variant="ghost" color="rgba(255,255,255,0.6)" _hover={{ color: '#CCF301', bg: 'rgba(255,255,255,0.05)' }} onClick={() => navigate('/tr/kaydedilenler')} borderRadius="6px">
                      <FiBookmark size={16} />
                    </IconButton>
                    <Box position="relative">
                      <IconButton aria-label="Mesajlar" variant="ghost" color="rgba(255,255,255,0.6)" _hover={{ color: '#CCF301', bg: 'rgba(255,255,255,0.05)' }} onClick={() => navigate('/tr/mesajlar')} borderRadius="6px">
                        <FiMessageSquare size={16} />
                      </IconButton>
                      {unreadCount > 0 && (
                        <Box
                          position="absolute" top="-2px" right="-2px"
                          w={4} h={4} bg="#CCF301" borderRadius="full"
                          display="flex" alignItems="center" justifyContent="center"
                        >
                          <Text fontSize="9px" fontWeight="700" color="black">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </>
                )}
                <IconButton aria-label="Profil" variant="ghost" color="rgba(255,255,255,0.6)" _hover={{ color: 'white', bg: 'rgba(255,255,255,0.05)' }} onClick={() => navigate('/tr/ayarlar')} borderRadius="6px">
                  {user.avatar_url ? (
                    <Box w={6} h={6} borderRadius="full" overflow="hidden" border="1.5px solid #CCF301">
                      <img src={user.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  ) : (
                    <FiUser size={16} />
                  )}
                </IconButton>
                <IconButton aria-label="Çıkış" variant="ghost" color="rgba(255,255,255,0.4)" _hover={{ color: 'red.400', bg: 'rgba(255,255,255,0.05)' }} onClick={() => signout().then(() => navigate('/tr/giris'))} borderRadius="6px">
                  <FiLogOut size={15} />
                </IconButton>
              </HStack>
            </>
          ) : (
            <HStack gap={2} display={{ base: 'none', sm: 'flex' }}>
              <Button asChild variant="ghost" size="sm" color="rgba(255,255,255,0.6)" _hover={{ color: 'white' }} borderRadius="6px" fontWeight="400">
                <Link to="/tr/giris">Giriş Yap</Link>
              </Button>
              <Button
                asChild
                size="sm"
                bg="#CCF301"
                color="black"
                _hover={{ opacity: 0.9 }}
                borderRadius="6px"
                fontWeight="500"
              >
                <Link to="/tr/kayit">Üye Ol</Link>
              </Button>
            </HStack>
          )}

          <IconButton
            aria-label="Menü"
            display={{ base: 'flex', md: 'none' }}
            variant="ghost"
            color="rgba(255,255,255,0.6)"
            onClick={() => setMenuOpen((v) => !v)}
            borderRadius="6px"
          >
            {menuOpen ? <FiX size={18} /> : <FiMenu size={18} />}
          </IconButton>
        </HStack>
      </Flex>

      {/* Mobile menu */}
      {menuOpen && (
        <Box
          bg="rgba(0,0,0,0.98)"
          borderTop="1px solid rgba(255,255,255,0.06)"
          px={4}
          py={4}
          display={{ md: 'none' }}
          position="absolute"
          left={0}
          right={0}
          top={`${HEADER_HEIGHT}px`}
          style={{ backdropFilter: 'blur(16px)' }}
        >
          <VStack gap={1}>
            {NAV.map((link) => (
              <Button
                key={link.to}
                asChild
                variant="ghost"
                w="full"
                justifyContent="flex-start"
                color="rgba(255,255,255,0.7)"
                _hover={{ color: 'white', bg: 'rgba(255,255,255,0.05)' }}
                borderRadius="6px"
                onClick={() => setMenuOpen(false)}
              >
                <Link to={link.to}>{link.label}</Link>
              </Button>
            ))}
            {!user && (
              <>
                <Button asChild variant="ghost" w="full" justifyContent="flex-start" color="rgba(255,255,255,0.6)" onClick={() => setMenuOpen(false)} borderRadius="6px">
                  <Link to="/tr/giris">Giriş Yap</Link>
                </Button>
                <Button asChild bg="#CCF301" color="black" w="full" mt={1} onClick={() => setMenuOpen(false)} borderRadius="6px" fontWeight="500">
                  <Link to="/tr/kayit">Üye Ol</Link>
                </Button>
              </>
            )}
            {user && (
              <>
                {isPrintCompany ? (
                  <>
                    <Button asChild variant="ghost" w="full" justifyContent="flex-start" color="rgba(255,255,255,0.6)" onClick={() => setMenuOpen(false)} borderRadius="6px">
                      <Link to="/tr/firma">Firma Paneli</Link>
                    </Button>
                    <Button asChild variant="ghost" w="full" justifyContent="flex-start" color="rgba(255,255,255,0.6)" onClick={() => setMenuOpen(false)} borderRadius="6px">
                      <Link to="/tr/firma/mesajlar">
                        Mesajlar {companyUnreadCount > 0 ? `(${companyUnreadCount})` : ''}
                      </Link>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button asChild variant="ghost" w="full" justifyContent="flex-start" color="rgba(255,255,255,0.6)" onClick={() => setMenuOpen(false)} borderRadius="6px">
                      <Link to="/tr/siparislerim">Siparişlerim</Link>
                    </Button>
                    <Button asChild variant="ghost" w="full" justifyContent="flex-start" color="rgba(255,255,255,0.6)" onClick={() => setMenuOpen(false)} borderRadius="6px">
                      <Link to="/tr/kaydedilenler">Kaydedilenler</Link>
                    </Button>
                    <Button asChild variant="ghost" w="full" justifyContent="flex-start" color="rgba(255,255,255,0.6)" onClick={() => setMenuOpen(false)} borderRadius="6px">
                      <Link to="/tr/mesajlar">Mesajlar {unreadCount > 0 ? `(${unreadCount})` : ''}</Link>
                    </Button>
                    <Button asChild variant="ghost" w="full" justifyContent="flex-start" color="rgba(255,255,255,0.6)" onClick={() => setMenuOpen(false)} borderRadius="6px">
                      <Link to="/tr/komisyonlar">Komisyonlar</Link>
                    </Button>
                  </>
                )}
                <Button asChild variant="ghost" w="full" justifyContent="flex-start" color="rgba(255,255,255,0.6)" onClick={() => setMenuOpen(false)} borderRadius="6px">
                  <Link to="/tr/ayarlar">Profil Ayarları</Link>
                </Button>
              </>
            )}
          </VStack>
        </Box>
      )}
    </Box>
  )
}

function VStack({ children, gap }: { children: React.ReactNode; gap?: number }) {
  return <Box display="flex" flexDirection="column" gap={gap ? `${gap * 4}px` : undefined}>{children}</Box>
}
